import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Truck, 
  Box, 
  CheckCircle2, 
  ArrowUpRight,
  ArrowRight,
  Shield,
  Clock,
  MapPin,
  Users
} from 'lucide-react';

const Ground = () => {
  const [activeStep, setActiveStep] = useState(0);

  const vettingSteps = [
    { 
      title: 'Safety Score Verification', 
      desc: 'DOT SMS review, accident history analysis, and insurance validation. We only work with carriers maintaining 85+ CSA scores.',
      icon: Shield 
    },
    { 
      title: 'Asset Inspection', 
      desc: 'Physical trailer inspections, ELD compliance verification, and equipment age standards. Average fleet age under 5 years.',
      icon: Truck 
    },
    { 
      title: 'Reference Validation', 
      desc: 'Three-shipper reference checks, payment history verification, and on-time performance validation.',
      icon: CheckCircle2 
    },
  ];

  const ftlFeatures = [
    'Dedicated tractors from our owned fleet',
    'Real-time GPS tracking via ELD integration',
    '24/7 dispatch support from Belleville HQ',
    'Drop trailer programs for high-volume shippers',
    'Cross-border capable with PAPS/PARS pre-clearance',
  ];

  const ltlFeatures = [
    'Consolidation hubs in Toronto, Montreal, Chicago',
    'Multi-stop optimization with dynamic routing',
    'Cross-dock facilities for time-sensitive freight',
    'Freight class expertise and NMFC guidance',
    'Damage-free handling with 99.2% claim-free rate',
  ];

  return (
    <div className="min-h-screen bg-bone">
      {/* HERO */}
      <section className="relative pt-32 pb-20 bg-forest overflow-hidden">
        <div className="absolute inset-0 bg-dots" />
        {/* Animated background orbs */}
        <motion.div 
          className="absolute top-20 right-20 w-64 h-64 bg-amber/20 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.3, 0.2]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-20 left-10 w-48 h-48 bg-white/10 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.3, 1],
            x: [0, 30, 0]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl">
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="font-mono text-xs tracking-widest uppercase text-amber mb-4"
            >
              Fleet & Ground Logistics
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-display font-black text-display-lg text-white mb-6"
            >
              Asset-Backed Ground Transportation
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="font-body text-lg text-white/70 max-w-2xl"
            >
              Full Truckload and LTL services powered by our owned fleet of 40 tractors and 50 trailers. 
              When we commit capacity, we deliver — because we own the assets.
            </motion.p>
          </div>
        </div>
      </section>

      {/* FTL SECTION */}
      <section className="section-padding bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="font-mono text-xs tracking-widest uppercase text-forest-2 mb-4">
                Full Truckload
              </div>
              <h2 className="font-display font-black text-display-lg text-forest mb-6">
                Dedicated Capacity for Your Largest Shipments
              </h2>
              <p className="font-body text-ink/70 text-lg leading-relaxed mb-8">
                Our FTL service leverages our owned fleet to provide guaranteed capacity when you need it most. 
                No broker games, no last-minute cancellations — just reliable, asset-backed transportation 
                from Belleville to anywhere in Canada and the USA.
              </p>
              <ul className="space-y-4">
                {ftlFeatures.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-amber flex-shrink-0 mt-0.5" />
                    <span className="font-body text-ink/80">{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-[4/3] bg-forest-2 rounded-2xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1591768793355-74d04bb6608f?w=800&auto=format&fit=crop&q=80" 
                  alt="Tractor Trailer Fleet"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-amber p-6 rounded-xl">
                <div className="font-mono text-3xl font-bold text-forest">40+</div>
                <div className="font-body text-sm text-forest/70">Owned Tractors</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* LTL SECTION */}
      <section className="section-padding bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative order-2 lg:order-1"
            >
              <div className="aspect-[4/3] bg-forest-2 rounded-2xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1605745341112-85968b19335b?w=800&auto=format&fit=crop&q=80" 
                  alt="LTL Cross Dock"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-forest p-6 rounded-xl">
                <div className="font-mono text-3xl font-bold text-amber">50+</div>
                <div className="font-body text-sm text-white/70">Trailers</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="font-mono text-xs tracking-widest uppercase text-forest-2 mb-4">
                Less Than Truckload
              </div>
              <h2 className="font-display font-black text-display-lg text-forest mb-6">
                Cost-Effective Consolidation & Distribution
              </h2>
              <p className="font-body text-ink/70 text-lg leading-relaxed mb-8">
                Our LTL network combines owned assets with vetted partner capacity to provide 
                competitive rates without compromising service. Strategic consolidation hubs 
                optimize every mile.
              </p>
              <ul className="space-y-4">
                {ltlFeatures.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-amber flex-shrink-0 mt-0.5" />
                    <span className="font-body text-ink/80">{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CARRIER VETTING */}
      <section className="section-padding bg-forest">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="font-mono text-xs tracking-widest uppercase text-amber mb-4">
              Our Network
            </div>
            <h2 className="font-display font-black text-display-lg text-white">
              Rigorous Carrier Vetting
            </h2>
            <p className="font-body text-white/60 max-w-2xl mx-auto mt-4">
              When we supplement our owned fleet, every partner carrier undergoes our 
              three-step validation process.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {vettingSteps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`p-8 rounded-2xl cursor-pointer transition-all ${
                  activeStep === i 
                    ? 'bg-amber text-forest' 
                    : 'bg-forest-2 text-white hover:bg-forest-3'
                }`}
                onClick={() => setActiveStep(i)}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  activeStep === i ? 'bg-forest text-white' : 'bg-amber text-forest'
                }`}>
                  <step.icon className="w-6 h-6" />
                </div>
                <div className="font-mono text-xs mb-2 opacity-60">STEP {i + 1}</div>
                <h3 className="font-display font-bold text-xl mb-3">{step.title}</h3>
                <p className={`font-body text-sm leading-relaxed ${
                  activeStep === i ? 'text-forest/80' : 'text-white/70'
                }`}>
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Progress Indicator */}
          <div className="flex justify-center gap-2 mt-8">
            {vettingSteps.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveStep(i)}
                className={`h-1 rounded-full transition-all ${
                  activeStep === i ? 'w-12 bg-amber' : 'w-4 bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-display font-black text-display-lg text-forest mb-6">
            Ready to Move Your Freight?
          </h2>
          <p className="font-body text-ink/70 text-xl max-w-2xl mx-auto mb-10">
            Get a quote in minutes. Our dispatch team is standing by 24/7 to secure your capacity.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/quote" className="btn-primary">
              Get FTL Quote
              <ArrowUpRight className="w-5 h-5" />
            </a>
            <a href="/quote" className="btn-ghost">
              Get LTL Quote
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Ground;
